import { useEffect, useState } from "react";
import { createDebugLogger } from "@utils/debugLog";
import { type EmbedHostContext, startEmbedBridge } from "./embedBridge";
import { isEmbedded } from "./embedHost";

const log = createDebugLogger("embed:bridge-hook");

/**
 * Answers the host's handshake and keeps whatever it told us about the
 * Dataverse org around.
 *
 * Mounting this matters even with nothing reading the context yet: the host
 * repeats its opening message until the app answers, and gives up with an
 * error if it never does.
 *
 * The host's `theme` is deliberately *not* applied. Dataverse exposes no
 * supported API for a model-driven app's current appearance, so the loader can
 * only report the operating system preference — which this app can read for
 * itself. Wire it up here if a real host signal ever appears.
 */
export const useEmbedBridge = (): EmbedHostContext | null => {
  const [context, setContext] = useState<EmbedHostContext | null>(null);

  useEffect(() => {
    if (!isEmbedded()) {
      return;
    }

    const bridge = startEmbedBridge({
      onContext: (hostContext) => {
        log("host context received", { orgUrl: hostContext.orgUrl });
        setContext(hostContext);
      },
    });

    return () => bridge.stop();
  }, []);

  return context;
};
